/**
 * Generic CRUD module:
 *  - ProductsExportButton: dropdown to export catalog as JSON or CSV
 *  - CrudPage: list view + filters + delete/move; switches to ProductCardGrid for products
 *  - CrudForm: renders fields by type (text, select, textarea, array, specs, metrics,
 *    images, documents, solution_slugs)
 *
 * Extracted from AdminPanel.js so routing-level file stays small.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, ArrowRight, Loader2, Search, Download, Upload, RefreshCw, AlertTriangle, X } from 'lucide-react';
import { API, adminApi, ic, useAuth } from './AdminShell';
import ImageUploader from './ImageUploader';
import DocumentUploader from './DocumentUploader';
import SolutionsMultiSelect from './SolutionsMultiSelect';
import HwCategoriesMultiSelect from './HwCategoriesMultiSelect';
import ProductCardGrid from './ProductCardGrid';
import { buildDefaults } from './fields';

// ---------- Reusable combo (select with "__custom__" sentinel for free input) ----------
const ComboInput = ({ value, onChange, options, placeholder }) => {
  const [custom, setCustom] = useState(false);
  useEffect(() => {
    // When value comes in from parent and isn't in options, switch to custom
    if (value && !options.includes(value)) setCustom(true);
  }, [value, options]);
  if (custom) {
    return (
      <div className="flex gap-2">
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={ic} autoFocus />
        <button type="button" onClick={() => { setCustom(false); onChange(''); }} className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl">Listado</button>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={ic}>
        <option value="">— Selecciona —</option>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
      <button type="button" onClick={() => setCustom(true)} className="px-3 py-2 text-xs text-[#0EA5E9] hover:bg-sky-50 border border-sky-200 rounded-xl whitespace-nowrap">+ Otra</button>
    </div>
  );
};

// ---------- Reusable multi-checkbox ----------
const MultiCheck = ({ value, onChange, options }) => {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (o) => onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${on ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0EA5E9]/50'}`}
          >
            {on ? '✓ ' : ''}{o}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// ResyncMirrorsButton — walks all products and backfills the Category→Solution mirror.
// Two-step UX: click opens a dialog with a dry-run summary; confirm to execute.
// ============================================================================
export const ResyncMirrorsButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null); // dry-run payload
  const [done, setDone] = useState(null);       // real-run payload

  const run = async (confirm) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/products/resync-mirrors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm: !!confirm }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Error');
      if (confirm) { setDone(data); setSummary(null); }
      else setSummary(data);
    } catch (e) {
      toast.error(e.message || 'Error');
    }
    setBusy(false);
  };

  const openDialog = async () => {
    setOpen(true);
    setDone(null);
    await run(false);
  };

  const close = () => { setOpen(false); setSummary(null); setDone(null); };

  return (
    <>
      <button
        onClick={openDialog}
        disabled={busy}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#0A2540] bg-yellow-100 border border-yellow-300 rounded-lg hover:bg-yellow-200 disabled:opacity-50"
        data-testid="admin-resync-mirrors-btn"
        title="Aplicar mapping automático Category → Solution a TODOS los productos"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        Re-sync soluciones
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="resync-dialog">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>Re-sincronizar soluciones espejo</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Recorre todos los productos y agrega la <strong>solución espejo</strong> asociada a su categoría en <code className="text-[10px] bg-slate-100 px-1 rounded">solution_slugs</code>. Idempotente — sólo agrega las que falten.</p>
              </div>
            </div>

            {busy && !summary && !done && (
              <div className="p-6 text-center text-slate-500 text-sm">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Escaneando catálogo…
              </div>
            )}

            {summary && !done && (
              <>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm space-y-1 mb-4" data-testid="resync-summary">
                  <div className="flex justify-between"><span className="text-slate-500">Productos escaneados</span><span className="font-semibold tabular-nums">{summary.scanned}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Se actualizarán</span><span className="font-semibold text-[#0EA5E9] tabular-nums">{summary.updated}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Ya sincronizados</span><span className="font-semibold text-emerald-600 tabular-nums">{summary.untouched}</span></div>
                  {(summary.skipped_no_category > 0 || summary.skipped_no_mirror > 0) && (
                    <div className="flex justify-between"><span className="text-slate-500">Omitidos (sin category o mapping)</span><span className="font-semibold text-amber-600 tabular-nums">{summary.skipped_no_category + summary.skipped_no_mirror}</span></div>
                  )}
                </div>

                {summary.sample && summary.sample.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Preview (primeros {Math.min(summary.sample.length, 5)})</p>
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-white border border-slate-200 rounded-lg p-2">
                      {summary.sample.slice(0, 5).map((s) => (
                        <div key={s.id} className="text-[11px] flex items-center gap-2">
                          <span className="shrink-0 w-2 h-2 rounded-full bg-[#0EA5E9]"></span>
                          <span className="truncate text-slate-700">{s.name}</span>
                          <span className="text-slate-400 text-[10px] whitespace-nowrap">{s.category}</span>
                          <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />
                          <span className="text-[#0EA5E9] text-[10px] font-semibold whitespace-nowrap">+ {s.mirror_added}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancelar</button>
                  <button
                    onClick={() => run(true)}
                    disabled={busy || summary.updated === 0}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50"
                    data-testid="resync-confirm-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                    {summary.updated === 0 ? 'Nada que actualizar' : `Aplicar a ${summary.updated} productos`}
                  </button>
                </div>
              </>
            )}

            {done && (
              <>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm mb-4" data-testid="resync-done">
                  <p className="text-emerald-800 font-semibold mb-1">¡Listo!</p>
                  <p className="text-emerald-700">{done.message}</p>
                </div>
                <div className="flex justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-semibold text-white bg-[#0A2540] rounded-xl hover:bg-slate-800" data-testid="resync-close-btn">Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Export Button (JSON/CSV)
// ============================================================================
export const RepairProductsButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null); // dry-run payload
  const [done, setDone] = useState(null);       // real-run payload

  const run = async (confirm) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/products/repair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm: !!confirm }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Error');
      if (confirm) { setDone(data); setSummary(null); }
      else setSummary(data);
    } catch (e) {
      toast.error(e.message || 'Error');
    }
    setBusy(false);
  };

  const openDialog = async () => {
    setOpen(true);
    setDone(null);
    await run(false);
  };
  const close = () => { setOpen(false); setSummary(null); setDone(null); };

  return (
    <>
      <button
        onClick={openDialog}
        disabled={busy}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-emerald-600 border border-emerald-700 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        data-testid="admin-repair-products-btn"
        title="Reparar precios y fotos huérfanas (idempotente · backup automático)"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        Reparar precios/fotos
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="repair-dialog">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>Reparar precios y fotos</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Detecta y corrige: (a) <strong>imagen principal</strong> desincronizada con la galería, (b) productos sin <code className="text-[10px] bg-slate-100 px-1 rounded">image</code> que sí tienen <code className="text-[10px] bg-slate-100 px-1 rounded">images[]</code>, (c) <strong>price_display</strong> placeholder cuando hay un <code className="text-[10px] bg-slate-100 px-1 rounded">price</code> numérico real. Idempotente · backup automático antes de aplicar.</p>
              </div>
            </div>

            {busy && !summary && !done && (
              <div className="p-6 text-center text-slate-500 text-sm">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Escaneando catálogo…
              </div>
            )}

            {summary && !done && (
              <>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm space-y-1 mb-4" data-testid="repair-summary">
                  <div className="flex justify-between"><span className="text-slate-500">Productos escaneados</span><span className="font-semibold tabular-nums">{summary.scanned}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Total a actualizar</span><span className="font-semibold text-[#0EA5E9] tabular-nums">{summary.to_update}</span></div>
                  <div className="border-t border-slate-200 my-2" />
                  <div className="flex justify-between text-[12px]"><span className="text-slate-400">· Imagen desincronizada</span><span className="font-medium tabular-nums">{summary.counts?.image_desync || 0}</span></div>
                  <div className="flex justify-between text-[12px]"><span className="text-slate-400">· Imagen faltante (con galería)</span><span className="font-medium tabular-nums">{summary.counts?.image_missing || 0}</span></div>
                  <div className="flex justify-between text-[12px]"><span className="text-slate-400">· Price_display regenerable</span><span className="font-medium tabular-nums">{summary.counts?.price_display_regen || 0}</span></div>
                </div>

                {summary.sample && summary.sample.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Preview (primeros {Math.min(summary.sample.length, 5)})</p>
                    <div className="max-h-44 overflow-y-auto space-y-1.5 bg-white border border-slate-200 rounded-lg p-2">
                      {summary.sample.slice(0, 5).map((s) => (
                        <div key={s.id} className="text-[11px] flex items-start gap-2 leading-snug">
                          <span className="shrink-0 w-2 h-2 rounded-full bg-emerald-500 mt-1"></span>
                          <div className="min-w-0 flex-1">
                            <div className="text-slate-700 truncate"><strong>{s.name}</strong> <span className="text-slate-400">· {s.category}</span></div>
                            <div className="text-emerald-700 text-[10px] truncate">→ {(s.diagnostics || []).join(', ')}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancelar</button>
                  <button
                    onClick={() => run(true)}
                    disabled={busy || (summary.to_update || 0) === 0}
                    className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="repair-confirm-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                    {(summary.to_update || 0) === 0 ? 'Nada que reparar' : `Aplicar a ${summary.to_update} productos`}
                  </button>
                </div>
              </>
            )}

            {done && (
              <>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm mb-4" data-testid="repair-done">
                  <p className="text-emerald-800 font-semibold mb-1">¡Reparación completada!</p>
                  <p className="text-emerald-700 text-[12px]">{done.message}</p>
                  {done.backup_path && (
                    <p className="text-emerald-600 text-[10px] mt-2 font-mono break-all">📦 Backup: {done.backup_path}</p>
                  )}
                </div>
                <div className="flex justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-semibold text-white bg-[#0A2540] rounded-xl hover:bg-slate-800" data-testid="repair-close-btn">Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Bulk Price Loader (CSV roundtrip)
// 1. Download template (current prices) → fill in Excel/Sheets
// 2. Upload filled CSV → dry-run preview → confirm to apply
// ============================================================================
export const BulkPriceLoaderButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('idle'); // idle | preview | done
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/products/prices/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error ' + res.status);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `allcom-precios-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Plantilla descargada — ábrela en Excel y rellena las columnas new_price.');
    } catch (e) {
      toast.error('Error descargando la plantilla');
    }
    setBusy(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Solo se acepta .csv');
      return;
    }
    setPendingFile(file);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('confirm', 'false');
      const res = await fetch(`${API}/admin/products/prices/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Error');
      setPreview(data);
      setStep('preview');
    } catch (e) {
      toast.error(e.message || 'Error procesando CSV');
      setPendingFile(null);
    }
    setBusy(false);
  };

  const applyImport = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('confirm', 'true');
      const res = await fetch(`${API}/admin/products/prices/import`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Error');
      setDone(data);
      setStep('done');
      toast.success(data.message || 'Precios actualizados');
    } catch (e) {
      toast.error(e.message || 'Error aplicando');
    }
    setBusy(false);
  };

  const close = () => {
    setOpen(false); setStep('idle'); setPreview(null); setDone(null); setPendingFile(null);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] border border-sky-600 rounded-lg hover:bg-sky-600"
        data-testid="admin-bulk-prices-btn"
        title="Cargar precios masivamente desde un CSV"
      >
        <Upload className="w-4 h-4" />
        Cargar precios CSV
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="bulk-prices-dialog">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                <Upload className="w-5 h-5 text-[#0EA5E9]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>Cargar precios desde CSV</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">Descarga la plantilla con el catálogo actual, llena las columnas <code className="text-[10px] bg-slate-100 px-1 rounded">new_price</code> y <code className="text-[10px] bg-slate-100 px-1 rounded">new_price_original</code> en Excel, y vuelve a subir el archivo. Las filas en blanco no se tocan.</p>
              </div>
            </div>

            {step === 'idle' && (
              <div className="space-y-4">
                {/* Step 1: Download template */}
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#0A2540] text-white text-xs font-bold flex items-center justify-center shrink-0">1</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-[#0A2540] mb-1">Descarga la plantilla</h4>
                      <p className="text-xs text-slate-500 mb-3">CSV con UTF-8 BOM (Excel-friendly) con todos los productos y sus precios actuales.</p>
                      <button
                        onClick={downloadTemplate}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-[#0A2540] bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                        data-testid="bulk-prices-download-btn"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Descargar plantilla
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 2: Upload */}
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#0A2540] text-white text-xs font-bold flex items-center justify-center shrink-0">2</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-[#0A2540] mb-1">Sube el CSV con los nuevos precios</h4>
                      <p className="text-xs text-slate-500 mb-3">Te mostraremos un preview antes de aplicar.</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="bulk-prices-file-input"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600 disabled:opacity-50"
                        data-testid="bulk-prices-upload-btn"
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Seleccionar archivo
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 'preview' && preview && (
              <>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm space-y-1 mb-4" data-testid="bulk-prices-preview">
                  <div className="flex justify-between"><span className="text-slate-500">Filas en archivo</span><span className="font-semibold tabular-nums">{preview.scanned}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Productos a actualizar</span><span className="font-semibold text-emerald-600 tabular-nums">{preview.parsed}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Errores</span><span className={`font-semibold tabular-nums ${preview.errors?.length ? 'text-red-500' : 'text-slate-400'}`}>{preview.errors?.length || 0}</span></div>
                </div>

                {preview.errors && preview.errors.length > 0 && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 max-h-40 overflow-y-auto">
                    <p className="font-semibold mb-2">Errores detectados:</p>
                    <ul className="space-y-1 font-mono text-[10px]">
                      {preview.errors.slice(0, 20).map((e, i) => (
                        <li key={i}>· Fila {e.row || '-'} · {e.slug || '?'}: {e.error}</li>
                      ))}
                      {preview.errors.length > 20 && <li>… y {preview.errors.length - 20} más</li>}
                    </ul>
                  </div>
                )}

                {preview.sample && preview.sample.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Cambios (primeros {Math.min(preview.sample.length, 10)})</p>
                    <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {preview.sample.slice(0, 10).map((s) => (
                        <div key={s.slug} className="p-2.5 text-[11px] bg-white">
                          <div className="font-semibold text-[#0A2540] truncate">{s.name}</div>
                          <div className="flex items-center gap-2 mt-1 text-slate-500 tabular-nums">
                            <span className="line-through">{s.before?.price_display || '—'}</span>
                            <ArrowRight className="w-3 h-3" />
                            <span className="text-emerald-600 font-semibold">{s.after?.price_display || s.after?.price}</span>
                            {s.after?.price_original && <span className="text-[10px] text-slate-400">/ original {s.after.price_original}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancelar</button>
                  <button
                    onClick={applyImport}
                    disabled={busy || (preview.parsed || 0) === 0}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 disabled:opacity-50"
                    data-testid="bulk-prices-confirm-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                    {(preview.parsed || 0) === 0 ? 'Nada que aplicar' : `Aplicar a ${preview.parsed} productos`}
                  </button>
                </div>
              </>
            )}

            {step === 'done' && done && (
              <>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm mb-4" data-testid="bulk-prices-done">
                  <p className="text-emerald-800 font-semibold mb-1">¡Precios actualizados!</p>
                  <p className="text-emerald-700 text-[12px]">{done.message}</p>
                </div>
                <div className="flex justify-end">
                  <button onClick={close} className="px-4 py-2 text-sm font-semibold text-white bg-[#0A2540] rounded-xl hover:bg-slate-800" data-testid="bulk-prices-close-btn">Cerrar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Export Button (JSON/CSV)
// ============================================================================
export const ProductsExportButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setBusy(true);
    setOpen(false);
    try {
      const res = await fetch(`${API}/admin/products/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error ' + res.status);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `allcom_catalog_${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Catálogo exportado (${format.toUpperCase()})`);
    } catch (e) {
      toast.error('Error exportando catálogo');
    }
    setBusy(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0A2540] rounded-lg hover:bg-[#050E1F] disabled:opacity-50"
        data-testid="admin-products-export"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Exportar catálogo
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-slate-200 rounded-xl shadow-xl p-2">
          <button onClick={() => download('json')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-sky-50 text-slate-700" data-testid="admin-products-export-json">
            <div className="font-semibold">JSON completo</div>
            <div className="text-[10px] text-slate-500">Ideal para agente IA externo</div>
          </button>
          <button onClick={() => download('csv')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-sky-50 text-slate-700" data-testid="admin-products-export-csv">
            <div className="font-semibold">CSV</div>
            <div className="text-[10px] text-slate-500">Compatible con Excel</div>
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CrudForm
// ============================================================================
export const CrudForm = ({ item, fields, onSave, onCancel, title, categories }) => {
  const auth = useAuth();
  // If creating (item has no id), hydrate with defaults so required fields never start blank.
  const isNew = !item || !item.id;
  const [form, setForm] = useState(() => (isNew ? { ...buildDefaults(fields), ...(item || {}) } : (item || {})));
  const [textAreas, setTextAreas] = useState({});

  useEffect(() => {
    const ta = {};
    fields.forEach((f) => {
      if (f.type === 'array' && item?.[f.key]) ta[f.key] = item[f.key].join('\n');
      if (f.type === 'specs' && item?.[f.key]) ta[f.key] = Object.entries(item[f.key]).map(([k, v]) => `${k}: ${v}`).join('\n');
      if (f.type === 'metrics' && item?.[f.key]) ta[f.key] = item[f.key].map((m) => `${m.label}: ${m.value}`).join('\n');
      if (f.type === 'release_items' && item?.[f.key]) ta[f.key] = item[f.key].map((m) => `${m.tag}: ${m.text}`).join('\n');
    });
    setTextAreas(ta);
  }, [item, fields]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    fields.forEach((f) => {
      if (f.type === 'array' && textAreas[f.key] !== undefined) data[f.key] = textAreas[f.key].split('\n').filter(Boolean);
      if (f.type === 'specs' && textAreas[f.key] !== undefined) {
        const specs = {};
        textAreas[f.key].split('\n').filter(Boolean).forEach((line) => {
          const [k, ...v] = line.split(':');
          if (k) specs[k.trim()] = v.join(':').trim();
        });
        data[f.key] = specs;
      }
      if (f.type === 'metrics' && textAreas[f.key] !== undefined) {
        data[f.key] = textAreas[f.key].split('\n').filter(Boolean).map((line) => {
          const [label, ...v] = line.split(':');
          return { label: label?.trim(), value: v.join(':').trim() };
        });
      }
      if (f.type === 'release_items' && textAreas[f.key] !== undefined) {
        data[f.key] = textAreas[f.key].split('\n').filter(Boolean).map((line) => {
          const [tag, ...v] = line.split(':');
          return { tag: tag?.trim(), text: v.join(':').trim() };
        }).filter((it) => it.tag && it.text);
      }
      if (f.type === 'model_type' && typeof data[f.key] === 'string') {
        data[f.key] = data[f.key].split(',').map((s) => s.trim()).filter(Boolean);
      }
      // Boolean coercion for select fields with 'true'/'false' string options
      if (f.type === 'select' && (data[f.key] === 'true' || data[f.key] === 'false')) {
        data[f.key] = data[f.key] === 'true';
      }
      // display_order coercion to int when present as string
      if (f.key === 'display_order' && typeof data[f.key] === 'string' && data[f.key]) {
        const n = parseInt(data[f.key], 10);
        if (!Number.isNaN(n)) data[f.key] = n;
      }
    });
    // Anti-orphan client-side validation: required fields cannot be empty.
    for (const f of fields) {
      if (!f.required) continue;
      const v = data[f.key];
      const empty = v === undefined || v === null || v === ''
        || (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error(`"${f.label}" es obligatorio`);
        return;
      }
    }
    // Price-shaped fields validation: must be empty, the placeholder, or parseable to a positive integer
    const priceKeys = ['price', 'price_original'];
    for (const key of priceKeys) {
      const raw = data[key];
      if (raw === undefined || raw === null || raw === '') continue;
      const s = String(raw).trim();
      if (s === '$9.999.999') continue; // legacy placeholder allowed
      const digits = s.replace(/[^\d]/g, '');
      if (!digits || parseInt(digits, 10) <= 0) {
        toast.error(`"${key}" debe ser un número válido (ej: 2490000) o dejarlo vacío`);
        return;
      }
    }
    if (Array.isArray(data.images) && data.images.length > 0 && !data.image) {
      data.image = data.images[0].url;
    }
    onSave(data);
  };

  return (
    <div>
      <button onClick={onCancel} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" />Volver a {title}
      </button>
      <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Syne,sans-serif' }}>
        {form.id ? 'Editar' : 'Nuevo'} {title.slice(0, -1)}
      </h2>
      <form onSubmit={handleSubmit} className="max-w-3xl space-y-4 bg-white p-6 rounded-xl border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.filter((f) => f.type === 'text' || f.type === 'select' || f.type === 'combo' || f.type === 'model_type').map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-slate-500 mb-1 block">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              {f.type === 'select' ? (
                <select value={form[f.key] ?? ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={ic} required={f.required} data-testid={`field-${f.key}`}>
                  <option value="">— Selecciona —</option>
                  {(f.options || categories || []).map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              ) : f.type === 'combo' ? (
                <ComboInput
                  value={form[f.key] ?? ''}
                  onChange={(v) => setForm({ ...form, [f.key]: v })}
                  options={f.options || []}
                  placeholder={f.label}
                />
              ) : (
                <input placeholder={f.label} value={form[f.key] ?? ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} className={ic} data-testid={`field-${f.key}`} />
              )}
              {f.hint && <p className="text-[10px] text-slate-400 mt-1">{f.hint}</p>}
            </div>
          ))}
        </div>
        {fields.filter((f) => f.type === 'multicheck').map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{f.label}</label>
            <MultiCheck value={form[f.key] || []} onChange={(v) => setForm({ ...form, [f.key]: v })} options={f.options || []} />
            {f.hint && <p className="text-[10px] text-slate-400 mt-1">{f.hint}</p>}
          </div>
        ))}
        {fields.filter((f) => f.type === 'textarea').map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{f.label}</label>
            <textarea placeholder={f.label} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={3} className={ic} />
          </div>
        ))}
        {fields.filter((f) => f.type === 'array' || f.type === 'specs' || f.type === 'metrics' || f.type === 'release_items').map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-500 mb-1 block">{f.label} <span className="text-slate-400">({f.hint})</span></label>
            <textarea value={textAreas[f.key] || ''} onChange={(e) => setTextAreas({ ...textAreas, [f.key]: e.target.value })} rows={f.type === 'release_items' ? 6 : 4} className={`${ic} ${f.type === 'specs' || f.type === 'release_items' ? 'font-mono text-xs' : ''}`} />
          </div>
        ))}
        {fields.filter((f) => f.type === 'images').map((f) => (
          <div key={f.key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <ImageUploader
              images={Array.isArray(form[f.key]) ? form[f.key] : []}
              onChange={(next) => setForm({ ...form, [f.key]: next })}
              token={auth.token}
              maxImages={10}
            />
          </div>
        ))}
        {fields.filter((f) => f.type === 'solution_slugs').map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              {f.label} {f.required && <span className="text-red-500">*</span>}
              {f.hint && <span className="text-slate-400 font-normal"> · {f.hint}</span>}
            </label>
            <SolutionsMultiSelect
              value={Array.isArray(form[f.key]) ? form[f.key] : []}
              onChange={(next) => setForm({ ...form, [f.key]: next })}
            />
          </div>
        ))}
        {fields.filter((f) => f.type === 'hw_slugs').map((f) => (
          <div key={f.key}>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              {f.label} {f.required && <span className="text-red-500">*</span>}
              {f.hint && <span className="text-slate-400 font-normal"> · {f.hint}</span>}
            </label>
            <HwCategoriesMultiSelect
              value={Array.isArray(form[f.key]) ? form[f.key] : []}
              onChange={(next) => setForm({ ...form, [f.key]: next })}
            />
          </div>
        ))}
        {fields.filter((f) => f.type === 'document').map((f) => (
          <div key={f.key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
            <DocumentUploader
              value={form[f.key] || null}
              onChange={(next) => setForm({ ...form, [f.key]: next })}
              token={auth.token}
              label={f.label}
              testid={f.key}
            />
            {f.hint && <p className="mt-2 text-[11px] text-slate-500">{f.hint}</p>}
          </div>
        ))}
        <div className="flex gap-3 pt-2">
          <button type="submit" className="px-6 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600">Guardar</button>
          <button type="button" onClick={onCancel} className="px-6 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancelar</button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// CrudPage (list + filters)
// ============================================================================

// Collections whose CRUD operations affect the public URL set (sitemap).
// Any create/update/delete on these collections triggers /admin/sitemap/regenerate.
const SITEMAP_AFFECTING_COLLECTIONS = new Set(['products', 'hw-categories', 'solutions', 'releases']);

const regenerateSitemap = async (token) => {
  try {
    await adminApi(token).post('/admin/sitemap/regenerate');
  } catch (err) {
    // Non-blocking: log a quiet warning, don't disrupt the admin flow
    // eslint-disable-next-line no-console
    console.warn('[admin] sitemap regen failed:', err?.response?.data?.detail || err.message);
  }
};

// Reusable confirmation dialog (replaces window.confirm — works in all browsers
// and gives full UX control: title, description, destructive style, item identity)
const ConfirmDialog = ({ open, title, message, details, confirmLabel = 'Eliminar', onConfirm, onCancel, busy }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="confirm-dialog">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Syne,sans-serif' }}>{title}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{message}</p>
              {details && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 font-mono break-all" data-testid="confirm-details">
                  {details}
                </div>
              )}
            </div>
            <button onClick={onCancel} disabled={busy} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg" aria-label="Cerrar">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50" data-testid="confirm-cancel-btn">
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={busy} className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="confirm-accept-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? 'Eliminando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const CrudPage = ({ title, collection, apiPath, fields, categories }) => {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState('');
  const [filterText, setFilterText] = useState('');
  const [movingItem, setMovingItem] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, slug, name }
  const [deleting, setDeleting] = useState(false);

  const fetchItems = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/${apiPath}`).then((r) => { setItems(r.data); setLoading(false); setReloadKey((k) => k + 1); }).catch(() => setLoading(false));
  }, [apiPath]);
  useEffect(fetchItems, [fetchItems]);

  // Open modal instead of window.confirm. Receives the FULL item so we can
  // show slug + name to the admin (prevents wrong-row deletes).
  const requestDelete = (item) => {
    if (!item) return;
    const id = item.id || item._id || item.slug;
    if (!id) {
      toast.error('Sin id válido — recarga la lista');
      return;
    }
    setPendingDelete({ id, slug: item.slug || '', name: item.name || item.title || item.headline || id });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await adminApi(auth.token).delete(`/admin/${collection}/${pendingDelete.id}`);
      toast.success(`"${pendingDelete.name}" eliminado`);
      setPendingDelete(null);
      fetchItems();
      // Regenerate sitemap if this collection affects public URLs
      if (SITEMAP_AFFECTING_COLLECTIONS.has(collection)) {
        regenerateSitemap(auth.token).then(() => {
          toast.success('Sitemap regenerado · URL huérfana removida');
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`No se pudo eliminar: ${detail}`);
    } finally {
      setDeleting(false);
    }
  };

  const moveItem = async (id, newCat) => {
    try {
      await adminApi(auth.token).put(`/admin/${collection}/${id}`, {
        category: newCat,
        category_display: newCat.replace(/-/g, ' ').toUpperCase(),
      });
      toast.success(`Movido a ${newCat}`);
      setMovingItem(null);
      fetchItems();
    } catch {
      toast.error('Error al mover');
    }
  };

  const saveItem = async (data) => {
    try {
      if (data.id) {
        await adminApi(auth.token).put(`/admin/${collection}/${data.id}`, data);
        toast.success('Actualizado');
      } else {
        await adminApi(auth.token).post(`/admin/${collection}`, data);
        toast.success('Creado');
      }
      setEditing(null);
      fetchItems();
      // Regenerate sitemap if this collection affects public URLs
      if (SITEMAP_AFFECTING_COLLECTIONS.has(collection)) {
        regenerateSitemap(auth.token);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`Error al guardar: ${detail}`);
    }
  };

  if (editing !== null) {
    return <CrudForm item={editing} fields={fields} onSave={saveItem} onCancel={() => setEditing(null)} title={title} categories={categories} />;
  }

  // Products use a visual drag-and-drop card grid instead of the generic table.
  if (collection === 'products') {
    return (
      <div data-testid={`admin-${collection}`}>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Syne,sans-serif' }}>{title}</h1>
          <div className="flex items-center gap-2">
            <RepairProductsButton token={auth.token} />
            <BulkPriceLoaderButton token={auth.token} />
            <ResyncMirrorsButton token={auth.token} />
            <ProductsExportButton token={auth.token} />
            <button onClick={() => setEditing({})} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600" data-testid="product-new-btn">
              <Plus className="w-4 h-4" />Nuevo
            </button>
          </div>
        </div>
        <ProductCardGrid token={auth.token} onEdit={(p) => setEditing(p)} onDeleted={fetchItems} reloadKey={reloadKey} />
      </div>
    );
  }

  let filtered = items;
  if (filterCat) filtered = filtered.filter((i) => i.category === filterCat);
  if (filterText) filtered = filtered.filter((i) => JSON.stringify(i).toLowerCase().includes(filterText.toLowerCase()));
  const uniqueCategories = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();

  return (
    <div data-testid={`admin-${collection}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Syne,sans-serif' }}>{title} ({filtered.length})</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing({})} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-lg hover:bg-sky-600">
            <Plus className="w-4 h-4" />Nuevo
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Buscar..." className="pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#0EA5E9]/30 w-64" />
        </div>
        {uniqueCategories.length > 1 && (
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="px-4 py-2.5 text-sm border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#0EA5E9]/30">
            <option value="">Todas las categorías</option>
            {uniqueCategories.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        )}
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <div key={item.id || item.slug} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:shadow-sm">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {item.category && <span className="text-[10px] px-2 py-0.5 bg-sky-50 text-[#0EA5E9] rounded-full font-medium shrink-0">{item.category}</span>}
                  {item.brand && <span className="text-[10px] text-slate-400 uppercase">{item.brand}</span>}
                  {item.industry && <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium">{item.industry}</span>}
                  {item.version && <span className="text-[11px] px-2 py-0.5 rounded-full font-bold tabular-nums shrink-0" style={{ background: (item.color || '#0EA5E9') + '22', color: item.color || '#0EA5E9' }}>{item.version}</span>}
                  {item.date && <span className="text-[10px] text-slate-400 uppercase tracking-wider">{item.date}</span>}
                  {item.published === false && <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-semibold uppercase">Borrador</span>}
                </div>
                <h3 className="text-sm font-semibold truncate">{item.name || item.title || item.headline}</h3>
                <p className="text-xs text-slate-500 truncate">{item.summary || item.description || item.subtitle || item.intro || ''}</p>
              </div>
              <div className="flex gap-1 ml-3 shrink-0">
                {categories && categories.length > 0 && (
                  <div className="relative">
                    <button onClick={() => setMovingItem(movingItem === item.id ? null : item.id)} className="p-2 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg" title="Mover de categoría">
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    {movingItem === item.id && (
                      <div className="absolute right-0 top-full mt-1 z-20 w-52 bg-white border border-slate-200 rounded-xl shadow-xl p-2 space-y-1">
                        <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider px-2 py-1">Mover a:</p>
                        {categories.map((c) => (
                          <button key={c} onClick={() => moveItem(item.id, c)} className={`w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-sky-50 hover:text-[#0EA5E9] ${item.category === c ? 'bg-sky-50 text-[#0EA5E9] font-semibold' : 'text-slate-600'}`}>{c}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => setEditing(item)} className="p-2 text-slate-400 hover:text-[#0EA5E9] hover:bg-sky-50 rounded-lg" data-testid={`crud-edit-btn-${item.slug || item.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => requestDelete(item)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`crud-delete-btn-${item.slug || item.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-slate-400 bg-white rounded-xl border border-slate-200">No hay elementos. Crea el primero.</div>}
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        title="¿Eliminar este elemento?"
        message={`Esta acción es irreversible. ${SITEMAP_AFFECTING_COLLECTIONS.has(collection) ? 'La URL pública se removerá automáticamente del sitemap.xml.' : ''}`}
        details={pendingDelete ? `${pendingDelete.name}${pendingDelete.slug ? `  ·  slug: ${pendingDelete.slug}` : ''}` : ''}
        confirmLabel="Sí, eliminar"
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setPendingDelete(null)}
        busy={deleting}
      />
    </div>
  );
};
